import {
  GLOBAL_PREFIX,
  LAYOUT_CONTAINER_MEDIUM,
  LAYOUT_CONTAINER_SMALL,
  LAYOUT_EMPTY,
} from '@/router/constants';
import { fetchApplication } from '@/api/common';

// The Commercial Review website differs per environment (localhost, testweb.,
// production), so its base URL comes from the backend instead of being fixed.
export const CR_PRICING_PATH = '/nl/prijzen/#standaardpakketten';
// Used when the backend has no URL for it (or can't be reached), so the
// redirect never lands on a path of the app's own domain.
export const CR_WEBSITE_FALLBACK_URL = 'https://commercialreview.nl';


const routes = [
  {
    path: `${GLOBAL_PREFIX}/reset-password/:uuid`,
    name: 'common-reset-password',
    component: () => import('@/views/shared/ResetPasswordView.vue'),
    meta: {
      title: 'phrase.reset_password',
      layout: LAYOUT_CONTAINER_SMALL,
      anonymousAllowed: true,
      hideBreadcrumbs: true,
    },
  },
  {
    path: `${GLOBAL_PREFIX}/login`,
    name: 'common-login',
    component: () => import('@/views/shared/LoginView.vue'),
    meta: {
      title: 'word.login',
      layout: LAYOUT_EMPTY,
      anonymousAllowed: true,
      hideBreadcrumbs: true,
    },
  },
  {
    path: `${GLOBAL_PREFIX}/logout`,
    name: 'common-logout',
    component: () => import('@/views/shared/LogoutView.vue'),
    meta: {
      anonymousAllowed: true,
      layout: LAYOUT_EMPTY,
      hideBreadcrumbs: true,
    },
  },
  {
    path: `${GLOBAL_PREFIX}/register-account/:as?`,
    name: 'common-register-account',
    component: () => import('@/views/shared/RegisterAccountView.vue'),
    meta: {
      title: 'phrase.create_account',
      anonymousAllowed: true,
      layout: LAYOUT_EMPTY,
      hideBreadcrumbs: true,
    },
  },
  {
    path: `${GLOBAL_PREFIX}/register/:slug`,
    name: 'common-register-detail',
    component: () => import('@/views/shared/RegistrationTemplateDetailView.vue'),
    meta: {
      anonymousAllowed: true,
      layout: LAYOUT_CONTAINER_SMALL,
      hideBreadcrumbs: true,
    },
  },
  {
    path: `${GLOBAL_PREFIX}/organisation/invitations/:uuid`,
    name: 'organisation-member-invitation-accept',
    component: () => import('@/views/organisation/OrganisationMemberInvitationAcceptView.vue'),
    meta: {
      anonymousAllowed: true,
      layout: LAYOUT_CONTAINER_SMALL,
      overlay: 'light',
      hideBreadcrumbs: true,
    },
  },
  {
    // Screenforce still links here; the campaign sign-up page it opened was
    // replaced by the pricing page on the Commercial Review website.
    path: `${GLOBAL_PREFIX}/:path(register|register-research)/`,
    name: 'common-register-research',
    component: { render: () => null },
    beforeEnter: async () => {
      let websiteCrUrl = null;
      try {
        const response = await fetchApplication();
        websiteCrUrl = response?.data?.website_cr_url;
      } catch {
        // Fall back to the production website below.
      }
      window.location.replace(`${websiteCrUrl || CR_WEBSITE_FALLBACK_URL}${CR_PRICING_PATH}`);
      return false;
    },
    meta: {
      anonymousAllowed: true,
      layout: LAYOUT_EMPTY,
      hideBreadcrumbs: true,
    },
  },
  {
    path: `${GLOBAL_PREFIX}/message`,
    name: 'common-message',
    component: () => import('@/views/shared/MessageView.vue'),
    meta: {
      anonymousAllowed: true,
      layout: LAYOUT_CONTAINER_SMALL,
      bgColor: 'str-primary-lighter',
      hideBreadcrumbs: true,
    },
  },
  {
    // De Success-pagina van ui-v1. De backend stuurt het oude `/success/` hierheen
    // (#1068), en verzonden mails linken er nog naar. Een melding uit `?msg=` met
    // een knop naar de voordeur, die zelf op rol doorstuurt; `?nb=` laat die knop
    // weg.
    path: `${GLOBAL_PREFIX}/success`,
    name: 'common-success',
    redirect: (to) => ({
      name: 'common-message',
      query: {
        ...to.query,
        msg: to.query.msg || 'sentence.generic_success',
        ...(to.query.nb ? {} : { action: 'public-home-redirect' }),
      },
    }),
  },
  {
    // Invite link to fill in a research (FID-3.6); the link itself points
    // here directly.
    path: `${GLOBAL_PREFIX}/i/:code`,
    name: 'public-research-invite-link',
    component: () => import('@/views/public/ResearchInviteLinkView.vue'),
    meta: {
      title: 'word.research',
      anonymousAllowed: true,
      layout: LAYOUT_CONTAINER_SMALL,
      overlay: 'light',
      hideBreadcrumbs: true,
    },
  },
  {
    path: `${GLOBAL_PREFIX}/share/:uuid`,
    name: 'research-share-link',
    component: () => import('@/views/public/ResearchShareLinkView.vue'),
    meta: {
      title: 'word.dashboard',
      anonymousAllowed: true,
      layout: LAYOUT_CONTAINER_MEDIUM,
      hideBreadcrumbs: true,
    },
  },
  {
    path: GLOBAL_PREFIX,
    name: 'common-home',
    component: () => import('@/views/shared/HomeView.vue'),
    meta: {
      hideBreadcrumbs: true,
    },
  },
  {
    // Opened by `stramigo auth login`; deliberately not anonymousAllowed, so the
    // router guard sends logged-out users through the regular login first.
    path: `${GLOBAL_PREFIX}/cli-login`,
    name: 'common-cli-login',
    component: () => import('@/views/shared/CliLoginView.vue'),
    meta: {
      title: 'phrase.cli_login_title',
      layout: LAYOUT_CONTAINER_SMALL,
      hideBreadcrumbs: true,
    },
  },
  {
    path: `${GLOBAL_PREFIX}/agreement`,
    name: 'common-user-agreement',
    component: () => import('@/views/shared/UserAgreementView.vue'),
    meta: {
      title: 'word.terms',
      layout: LAYOUT_CONTAINER_SMALL,
      overlay: 'light',
      hideBreadcrumbs: true,
    },
  },
  {
    path: `${GLOBAL_PREFIX}/user-profile`,
    name: 'user-profile',
    component: () => import('@/views/shared/UserProfileView.vue'),
    meta: {
      title: 'phrase.my_account',
    },
  },
];

export default routes;
